import React, { useEffect, useState, useMemo } from "react";
import { X, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import RichTextEditor from "./RichTextEditor";
import {
  CATEGORIES,
  COURTS,
  AUTHORITIES,
  DOC_TYPES,
  URGENCY,
  CONTENT_TYPE_LABEL,
  slugify,
} from "@/lib/cmsConstants";

/*
 * Side drawer with type-specific forms for the CMS.
 * Renders a shared layout (title/status/category) then swaps the type-body.
 */
export default function CMSFormDrawer({
  open,
  contentType,
  initial,
  onSave,
  onCancel,
  saving,
}) {
  const isEdit = !!initial?.id;
  const [status, setStatus] = useState("draft");
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState([]);
  const [slug, setSlug] = useState("");
  const [fields, setFields] = useState({});

  useEffect(() => {
    if (!open) return;
    setStatus(initial?.status || "draft");
    setTitle(initial?.title || "");
    setCategory(initial?.category || []);
    setSlug(initial?.slug || "");
    setFields(initial?.fields ? { ...initial.fields } : {});
  }, [open, initial]);

  const setField = (k, v) => setFields((f) => ({ ...f, [k]: v }));

  const canSave = title.trim().length > 0 && !saving;

  const handleSave = (nextStatus) => {
    if (!canSave) {
      toast.error("Title is required");
      return;
    }
    const payload = {
      contentType,
      status: nextStatus || status,
      title: title.trim(),
      category,
      slug: contentType === "blog" ? slug || slugify(title) : null,
      fields,
    };
    onSave(payload);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" data-testid="cms-form-drawer">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={saving ? undefined : onCancel}
      />
      <div
        className="absolute right-0 top-0 h-full w-full sm:w-[720px] bg-white shadow-2xl flex flex-col"
        role="dialog"
      >
        <header className="flex items-center justify-between px-5 py-3.5 border-b border-[#E2E8F0]">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#64748B]">
              {isEdit ? "Edit" : "New"} · {CONTENT_TYPE_LABEL[contentType]}
            </div>
            <div className="text-base font-semibold text-[#0F172A]">
              {isEdit ? initial.title || "Untitled" : "Create content"}
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="h-9 w-9 rounded-md hover:bg-[#F1F5F9] inline-flex items-center justify-center"
            data-testid="cms-form-close"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-4">
          <SharedFields
            title={title}
            setTitle={(v) => {
              setTitle(v);
              if (contentType === "blog" && !isEdit) setSlug(slugify(v));
            }}
            status={status}
            setStatus={setStatus}
            category={category}
            setCategory={setCategory}
          />

          {contentType === "case_law" && (
            <CaseLawFields fields={fields} setField={setField} />
          )}
          {contentType === "act" && (
            <ActFields fields={fields} setField={setField} />
          )}
          {contentType === "circular" && (
            <CircularFields fields={fields} setField={setField} />
          )}
          {contentType === "alert" && (
            <AlertFields fields={fields} setField={setField} />
          )}
          {contentType === "blog" && (
            <BlogFields
              fields={fields}
              setField={setField}
              slug={slug}
              setSlug={setSlug}
            />
          )}
        </div>

        <footer className="px-5 py-3.5 border-t border-[#E2E8F0] flex items-center justify-between gap-3 bg-white">
          <div className="text-xs text-[#64748B]">
            {status === "published" ? "Published — visible to public" : "Draft — hidden"}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="h-9 px-3 rounded-md text-sm font-medium border border-[#E2E8F0] hover:bg-[#F8FAFC]"
              data-testid="cms-form-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => handleSave("draft")}
              disabled={!canSave}
              className="h-9 px-3 rounded-md text-sm font-medium border border-[#E2E8F0] hover:bg-[#F8FAFC] disabled:opacity-50"
              data-testid="cms-form-save-draft"
            >
              Save as Draft
            </button>
            <button
              type="button"
              onClick={() => handleSave("published")}
              disabled={!canSave}
              className="h-9 px-3 rounded-md text-sm font-medium bg-[#0F172A] text-white hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="cms-form-publish"
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save & Publish
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

// ---------- Shared fields ----------
function SharedFields({ title, setTitle, status, setStatus, category, setCategory }) {
  const toggleCat = (c) => {
    setCategory(
      category.includes(c) ? category.filter((x) => x !== c) : [...category, c]
    );
  };
  return (
    <div className="flex flex-col gap-3">
      <Field label="Title" required>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          data-testid="cms-field-title"
          className={inputCls}
          placeholder="Enter title"
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Status">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="cms-field-status"
            className={inputCls}
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </Field>
        <Field label="Categories">
          <div className="flex flex-wrap gap-1.5">
            {CATEGORIES.map((c) => {
              const on = category.includes(c);
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleCat(c)}
                  data-testid={`cms-cat-${slugify(c)}`}
                  className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
                    on
                      ? "bg-[#0F172A] text-white border-[#0F172A]"
                      : "bg-white text-[#334155] border-[#E2E8F0] hover:bg-[#F8FAFC]"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </Field>
      </div>
    </div>
  );
}

// ---------- Type-specific field groups ----------
function CaseLawFields({ fields, setField }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Court or Tribunal">
          <select
            value={fields.court || ""}
            onChange={(e) => setField("court", e.target.value)}
            className={inputCls}
          >
            <option value="">Select…</option>
            {COURTS.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </Field>
        <Field label="Date of Judgment">
          <input
            type="date"
            value={fields.judgmentDate || ""}
            onChange={(e) => setField("judgmentDate", e.target.value)}
            className={inputCls}
          />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Citation Number">
          <input
            value={fields.citation || ""}
            onChange={(e) => setField("citation", e.target.value)}
            className={inputCls}
            placeholder="e.g. 2024 ITR 45 SC"
          />
        </Field>
        <Field label="Jurisdiction">
          <input
            value={fields.jurisdiction || ""}
            onChange={(e) => setField("jurisdiction", e.target.value)}
            className={inputCls}
            placeholder="e.g. Delhi HC"
          />
        </Field>
      </div>
      <Field label="Summary" hint="3–5 sentence plain-English summary of the ruling.">
        <RichTextEditor
          value={fields.summary || ""}
          onChange={(v) => setField("summary", v)}
          minHeight={110}
        />
      </Field>
      <Field label="Full Judgment Text">
        <RichTextEditor
          value={fields.fullText || ""}
          onChange={(v) => setField("fullText", v)}
          minHeight={220}
        />
      </Field>
      <Field label="Key Principle" hint="Single most important legal principle established.">
        <textarea
          rows={2}
          value={fields.keyPrinciple || ""}
          onChange={(e) => setField("keyPrinciple", e.target.value)}
          className={inputCls}
          placeholder="e.g. Interest on refunds is compensatory, not taxable."
        />
      </Field>
      <Field label="Impact on Taxpayers">
        <textarea
          rows={2}
          value={fields.impact || ""}
          onChange={(e) => setField("impact", e.target.value)}
          className={inputCls}
        />
      </Field>
    </>
  );
}

function ActFields({ fields, setField }) {
  const sections = fields.sections || [];
  const setSections = (next) => setField("sections", next);
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Short Name / Popular Name">
          <input
            value={fields.shortName || ""}
            onChange={(e) => setField("shortName", e.target.value)}
            className={inputCls}
            placeholder="e.g. IT Act"
          />
        </Field>
        <Field label="Governing Authority">
          <select
            value={fields.authority || ""}
            onChange={(e) => setField("authority", e.target.value)}
            className={inputCls}
          >
            <option value="">Select…</option>
            {AUTHORITIES.map((a) => (
              <option key={a}>{a}</option>
            ))}
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Date of Enactment">
          <input
            type="date"
            value={fields.enactmentDate || ""}
            onChange={(e) => setField("enactmentDate", e.target.value)}
            className={inputCls}
          />
        </Field>
        <Field label="Date of Last Amendment">
          <input
            type="date"
            value={fields.amendmentDate || ""}
            onChange={(e) => setField("amendmentDate", e.target.value)}
            className={inputCls}
          />
        </Field>
      </div>
      <Field label="Description">
        <RichTextEditor
          value={fields.description || ""}
          onChange={(v) => setField("description", v)}
          minHeight={140}
        />
      </Field>
      <Field label="Key Sections">
        <div className="flex flex-col gap-2">
          {sections.map((s, i) => (
            <div
              key={i}
              className="grid grid-cols-[100px_1fr_auto] gap-2 items-start p-2 rounded-md border border-[#E2E8F0] bg-[#F8FAFC]"
            >
              <input
                value={s.number || ""}
                onChange={(e) => {
                  const next = sections.slice();
                  next[i] = { ...next[i], number: e.target.value };
                  setSections(next);
                }}
                className={inputCls}
                placeholder="Sec. #"
              />
              <div className="flex flex-col gap-1.5">
                <input
                  value={s.title || ""}
                  onChange={(e) => {
                    const next = sections.slice();
                    next[i] = { ...next[i], title: e.target.value };
                    setSections(next);
                  }}
                  className={inputCls}
                  placeholder="Section title"
                />
                <textarea
                  rows={2}
                  value={s.brief || ""}
                  onChange={(e) => {
                    const next = sections.slice();
                    next[i] = { ...next[i], brief: e.target.value };
                    setSections(next);
                  }}
                  className={inputCls}
                  placeholder="Brief description"
                />
              </div>
              <button
                type="button"
                onClick={() => setSections(sections.filter((_, j) => j !== i))}
                className="h-8 w-8 rounded-md inline-flex items-center justify-center text-[#DC2626] hover:bg-white"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              setSections([...sections, { number: "", title: "", brief: "" }])
            }
            className="h-9 rounded-md border border-dashed border-[#CBD5E1] text-sm text-[#334155] hover:bg-[#F8FAFC] inline-flex items-center justify-center gap-1.5"
          >
            <Plus className="h-3.5 w-3.5" /> Add section
          </button>
        </div>
      </Field>
      <Field label="Related Rules and Regulations">
        <input
          value={fields.related || ""}
          onChange={(e) => setField("related", e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Official Gazette Link">
        <input
          value={fields.gazetteUrl || ""}
          onChange={(e) => setField("gazetteUrl", e.target.value)}
          className={inputCls}
          placeholder="https://…"
        />
      </Field>
    </>
  );
}

function CircularFields({ fields, setField }) {
  const actions = fields.actions || [];
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Document Type">
          <select
            value={fields.docType || ""}
            onChange={(e) => setField("docType", e.target.value)}
            className={inputCls}
          >
            <option value="">Select…</option>
            {DOC_TYPES.map((d) => (
              <option key={d}>{d}</option>
            ))}
          </select>
        </Field>
        <Field label="Issuing Authority">
          <select
            value={fields.authority || ""}
            onChange={(e) => setField("authority", e.target.value)}
            className={inputCls}
          >
            <option value="">Select…</option>
            {AUTHORITIES.map((a) => (
              <option key={a}>{a}</option>
            ))}
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Reference Number">
          <input
            value={fields.refNumber || ""}
            onChange={(e) => setField("refNumber", e.target.value)}
            className={inputCls}
            placeholder="Circular No. 15 of 2025"
          />
        </Field>
        <Field label="Date of Issue">
          <input
            type="date"
            value={fields.issueDate || ""}
            onChange={(e) => setField("issueDate", e.target.value)}
            className={inputCls}
          />
        </Field>
        <Field label="Date of Effect">
          <input
            type="date"
            value={fields.effectDate || ""}
            onChange={(e) => setField("effectDate", e.target.value)}
            className={inputCls}
          />
        </Field>
      </div>
      <Field label="Subject Line">
        <input
          value={fields.subject || ""}
          onChange={(e) => setField("subject", e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Full Text">
        <RichTextEditor
          value={fields.fullText || ""}
          onChange={(v) => setField("fullText", v)}
          minHeight={200}
        />
      </Field>
      <Field label="Plain-Language Summary" hint="3–5 sentence explanation.">
        <RichTextEditor
          value={fields.plainSummary || ""}
          onChange={(v) => setField("plainSummary", v)}
          minHeight={120}
        />
      </Field>
      <Field label="Action Required (bullet list)">
        <BulletList
          items={actions}
          onChange={(next) => setField("actions", next)}
          placeholder="e.g. File revised return within 30 days"
        />
      </Field>
    </>
  );
}

function AlertFields({ fields, setField }) {
  const actions = fields.actions || [];
  const links = fields.links || [];
  return (
    <>
      <Field
        label="Headline"
        hint="Short punch — max 120 characters."
      >
        <input
          maxLength={120}
          value={fields.headline || ""}
          onChange={(e) => setField("headline", e.target.value)}
          className={inputCls}
        />
      </Field>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Urgency">
          <div className="flex gap-1.5">
            {URGENCY.map((u) => {
              const active = fields.urgencyLevel === u.key;
              return (
                <button
                  key={u.key}
                  type="button"
                  onClick={() => setField("urgencyLevel", u.key)}
                  className={`flex-1 h-9 rounded-md text-xs font-semibold uppercase tracking-wider border transition-all`}
                  style={{
                    background: active ? u.color : u.bg,
                    color: active ? "#fff" : u.color,
                    borderColor: active ? u.color : "#E2E8F0",
                  }}
                >
                  {u.label}
                </button>
              );
            })}
          </div>
        </Field>
        <Field label="Effective Date">
          <input
            type="date"
            value={fields.effectiveDate || ""}
            onChange={(e) => setField("effectiveDate", e.target.value)}
            className={inputCls}
          />
        </Field>
        <Field label="Expiry Date (optional)">
          <input
            type="date"
            value={fields.expiryDate || ""}
            onChange={(e) => setField("expiryDate", e.target.value)}
            className={inputCls}
          />
        </Field>
      </div>
      <Field label="Issuing Authority">
        <input
          value={fields.authority || ""}
          onChange={(e) => setField("authority", e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Body">
        <RichTextEditor
          value={fields.body || ""}
          onChange={(v) => setField("body", v)}
          minHeight={140}
        />
      </Field>
      <Field label="Action Required (bullet list)">
        <BulletList
          items={actions}
          onChange={(next) => setField("actions", next)}
          placeholder="e.g. File Form 26Q for Q3 by 31 Jan"
        />
      </Field>
      <Field label="Relevant Links">
        <div className="flex flex-col gap-2">
          {links.map((l, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2">
              <input
                value={l.label || ""}
                onChange={(e) => {
                  const next = links.slice();
                  next[i] = { ...next[i], label: e.target.value };
                  setField("links", next);
                }}
                className={inputCls}
                placeholder="Label"
              />
              <input
                value={l.url || ""}
                onChange={(e) => {
                  const next = links.slice();
                  next[i] = { ...next[i], url: e.target.value };
                  setField("links", next);
                }}
                className={inputCls}
                placeholder="https://…"
              />
              <button
                type="button"
                onClick={() => setField("links", links.filter((_, j) => j !== i))}
                className="h-9 w-9 rounded-md inline-flex items-center justify-center text-[#DC2626] hover:bg-[#FEF2F2]"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setField("links", [...links, { label: "", url: "" }])}
            className="h-9 rounded-md border border-dashed border-[#CBD5E1] text-sm text-[#334155] hover:bg-[#F8FAFC] inline-flex items-center justify-center gap-1.5"
          >
            <Plus className="h-3.5 w-3.5" /> Add link
          </button>
        </div>
      </Field>
    </>
  );
}

function BlogFields({ fields, setField, slug, setSlug }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Slug (URL)" hint="Auto-generated from title; editable.">
          <input
            value={slug}
            onChange={(e) => setSlug(slugify(e.target.value))}
            className={inputCls}
          />
        </Field>
        <Field label="Publish Date">
          <input
            type="date"
            value={fields.publishDate || ""}
            onChange={(e) => setField("publishDate", e.target.value)}
            className={inputCls}
          />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Author Name">
          <input
            value={fields.author || "NDM Advisors"}
            onChange={(e) => setField("author", e.target.value)}
            className={inputCls}
          />
        </Field>
        <Field label="Author Designation">
          <input
            value={fields.designation || ""}
            onChange={(e) => setField("designation", e.target.value)}
            className={inputCls}
            placeholder="Senior Tax Consultant"
          />
        </Field>
      </div>
      <Field label="Cover Image URL" hint="Optional — https://…">
        <input
          value={fields.coverImage || ""}
          onChange={(e) => setField("coverImage", e.target.value)}
          className={inputCls}
          placeholder="https://…"
        />
        {fields.coverImage && (
          <img
            src={fields.coverImage}
            alt="Cover preview"
            className="mt-2 h-24 w-40 object-cover rounded-md border border-[#E2E8F0]"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        )}
      </Field>
      <Field label="Tags" hint="Comma-separated.">
        <input
          value={fields.tags || ""}
          onChange={(e) => setField("tags", e.target.value)}
          className={inputCls}
          placeholder="tds, deductor, cbdt"
        />
      </Field>
      <Field label="Meta Description" hint="Under 160 chars for SEO.">
        <input
          maxLength={160}
          value={fields.metaDescription || ""}
          onChange={(e) => setField("metaDescription", e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Body">
        <RichTextEditor
          value={fields.body || ""}
          onChange={(v) => setField("body", v)}
          minHeight={260}
        />
      </Field>
      <label className="inline-flex items-center gap-2 text-sm text-[#0F172A] cursor-pointer">
        <input
          type="checkbox"
          checked={!!fields.featured}
          onChange={(e) => setField("featured", e.target.checked)}
        />
        Pin as Featured (shown on landing page)
      </label>
    </>
  );
}

// ---------- Small primitives ----------
const inputCls =
  "w-full h-9 px-3 rounded-md border border-[#E2E8F0] bg-white text-[13px] outline-none focus:border-[#0F172A] transition-colors";

function Field({ label, children, required, hint }) {
  return (
    <label className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5">
        <span className="text-[12px] font-medium text-[#334155]">
          {label}
          {required && <span className="text-[#DC2626] ml-0.5">*</span>}
        </span>
        {hint && <span className="text-[11px] text-[#94A3B8]">— {hint}</span>}
      </div>
      {children}
    </label>
  );
}

function BulletList({ items, onChange, placeholder }) {
  return (
    <div className="flex flex-col gap-2">
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-[1fr_auto] gap-2">
          <input
            value={it}
            onChange={(e) => {
              const next = items.slice();
              next[i] = e.target.value;
              onChange(next);
            }}
            className={inputCls}
            placeholder={placeholder}
          />
          <button
            type="button"
            onClick={() => onChange(items.filter((_, j) => j !== i))}
            className="h-9 w-9 rounded-md inline-flex items-center justify-center text-[#DC2626] hover:bg-[#FEF2F2]"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...items, ""])}
        className="h-9 rounded-md border border-dashed border-[#CBD5E1] text-sm text-[#334155] hover:bg-[#F8FAFC] inline-flex items-center justify-center gap-1.5"
      >
        <Plus className="h-3.5 w-3.5" /> Add item
      </button>
    </div>
  );
}
